import { useState, useEffect, useCallback, useMemo } from 'react'
import { getMessages, sendMessage, replyToThread, markThreadRead } from '../api/library'

/**
 * Hook for loading and interacting with bookseller message threads.
 *
 * Backend prerequisites (deferred to backend-api-dev):
 * - POST /api/v1/me/messages/:id/reply
 * - PATCH /api/v1/me/messages/:id/read
 * - lastReadAt field exposed on each thread
 */
export function useBooksellerMessages() {
  const [threads, setThreads] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    getMessages()
      .then((data) => {
        if (!cancelled) setThreads(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const send = useCallback(async ({ subject, body }) => {
    const thread = await sendMessage({ subject, body })
    setThreads((prev) => [thread, ...prev])
    return thread
  }, [])

  const reply = useCallback(async (threadId, body) => {
    const updated = await replyToThread(threadId, body)
    setThreads((prev) =>
      prev.map((thread) => (thread.id === threadId ? updated : thread))
    )
    return updated
  }, [])

  const markRead = useCallback(async (threadId) => {
    const previous = threads.find((t) => t.id === threadId)
    const now = new Date().toISOString()

    // Optimistically update local state for immediate UI feedback.
    setThreads((prev) =>
      prev.map((thread) =>
        thread.id === threadId ? { ...thread, lastReadAt: now } : thread
      )
    )

    try {
      const updated = await markThreadRead(threadId)
      setThreads((prev) =>
        prev.map((thread) => (thread.id === threadId ? updated : thread))
      )
      return updated
    } catch (err) {
      // Revert optimistic update on failure.
      if (previous) {
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId
              ? { ...thread, lastReadAt: previous.lastReadAt }
              : thread
          )
        )
      }
      throw err
    }
  }, [threads])

  const unreadCount = useMemo(() => {
    return threads.filter((thread) => {
      const latestBooksellerMessage = [...(thread.messages || [])]
        .reverse()
        .find((m) => m.from === 'bookseller')
      if (!latestBooksellerMessage?.sentAt) return false
      const lastRead = thread.lastReadAt
      if (!lastRead) return true
      return new Date(latestBooksellerMessage.sentAt) > new Date(lastRead)
    }).length
  }, [threads])

  return { threads, loading, error, send, reply, markRead, unreadCount }
}
